import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

/** 클래스 이어 붙이기 — 빈 값은 건너뛴다 */
function cn(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps {
  label: string;
  value: string;
  options: readonly SelectOption[];
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  name?: string;
  className?: string;
  triggerClassName?: string;
  /**
   * outline — 옅은 테두리 (입력 폼 기본).
   * filled — 테두리 없이 회색 배경 (목록 위 필터처럼 화면을 고르는 곳, 토스 스타일)
   */
  appearance?: 'outline' | 'filled';
}

/**
 * 공용 셀렉트 — 웹 `apps/web/src/components/select.tsx` 와 같은 동작(키보드·검색·위아래 자동 배치).
 * 브라우저 기본 목록 대신 직접 그린 목록이라 창(Dialog) 안에서도 잘리지 않는다. 겉모습은 어드민 입력칸(inputCls)에 맞춘다
 */
export function Select({
  label,
  value,
  options,
  onValueChange,
  placeholder = '선택하세요',
  disabled = false,
  required = false,
  id: providedId,
  name,
  className,
  triggerClassName,
  appearance = 'outline',
}: SelectProps) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const anchorRect = useRef<DOMRect | null>(null);
  const menu = useRef<HTMLUListElement>(null);
  const search = useRef({ text: '', time: 0 });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [invalid, setInvalid] = useState(false);
  const [position, setPosition] = useState({ left: 0, top: 0, width: 0, maxHeight: 320 });
  const selected = options.find((option) => option.value === value);
  const expanded = open && !disabled;

  useLayoutEffect(() => {
    if (!expanded) return;
    const list = menu.current;
    // A DOM descendant popover participates in the top layer of its containing modal too.
    if (list?.showPopover && !list.matches(':popover-open')) list.showPopover();
    const option = list?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    if (list && option) {
      // Only scroll the popup, never its page or enclosing dialog.
      if (option.offsetTop < list.scrollTop) list.scrollTop = option.offsetTop;
      else if (option.offsetTop + option.offsetHeight > list.scrollTop + list.clientHeight)
        list.scrollTop = option.offsetTop + option.offsetHeight - list.clientHeight;
    }
  }, [expanded, active]);

  useEffect(() => {
    if (!expanded) return;
    function outside(event: Event) {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    }
    // A scroll queued before the click can arrive after opening, and locking the page scroll hides
    // the scrollbar (the viewport widens and fires resize) while the trigger stays put.
    // Close only if the anchor actually moved or changed size.
    function closeIfMoved() {
      const rect = trigger.current?.getBoundingClientRect();
      const anchor = anchorRect.current;
      if (
        rect &&
        anchor &&
        (Math.abs(rect.top - anchor.top) > 1 ||
          Math.abs(rect.left - anchor.left) > 1 ||
          Math.abs(rect.width - anchor.width) > 1)
      )
        setOpen(false);
    }
    function scroll(event: Event) {
      // Scrolling a long option list must not dismiss it (including keyboard scrolling).
      if (event.target instanceof Node && menu.current?.contains(event.target)) return;
      closeIfMoved();
    }
    document.addEventListener('pointerdown', outside);
    document.addEventListener('focusin', outside);
    window.addEventListener('scroll', scroll, true);
    window.addEventListener('resize', closeIfMoved);
    window.visualViewport?.addEventListener('resize', closeIfMoved);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('focusin', outside);
      window.removeEventListener('scroll', scroll, true);
      window.removeEventListener('resize', closeIfMoved);
      window.visualViewport?.removeEventListener('resize', closeIfMoved);
    };
  }, [expanded]);

  function show(index = options.findIndex((option) => option.value === value && !option.disabled)) {
    const rect = trigger.current?.getBoundingClientRect();
    if (disabled || trigger.current?.matches(':disabled') || !rect) return;
    anchorRect.current = rect;
    const viewport = window.visualViewport;
    const viewportTop = viewport?.offsetTop ?? 0;
    const viewportLeft = viewport?.offsetLeft ?? 0;
    const viewportHeight = viewport?.height ?? window.innerHeight;
    const viewportWidth = viewport?.width ?? window.innerWidth;
    const gap = 6;
    const padding = 8;
    const desiredHeight = Math.min(320, options.length * 38 + 12);
    const below = viewportTop + viewportHeight - rect.bottom - gap - padding;
    const above = rect.top - viewportTop - gap - padding;
    const upwards = below < desiredHeight && above > below;
    const maxHeight = Math.max(0, Math.min(desiredHeight, upwards ? above : below));
    const width = Math.min(rect.width, viewportWidth - padding * 2);
    setPosition({
      left: Math.max(
        viewportLeft + padding,
        Math.min(rect.left, viewportLeft + viewportWidth - width - padding),
      ),
      top: upwards ? rect.top - gap - maxHeight : rect.bottom + gap,
      width,
      maxHeight,
    });
    setActive(index >= 0 ? index : options.findIndex((option) => !option.disabled));
    search.current = { text: '', time: 0 };
    setOpen(true);
  }

  function select(index: number) {
    const option = options[index];
    if (disabled || !option || option.disabled) return;
    onValueChange(option.value);
    setInvalid(false);
    setOpen(false);
    trigger.current?.focus({ preventScroll: true });
  }

  function move(direction: 1 | -1) {
    for (let index = active + direction; index >= 0 && index < options.length; index += direction) {
      if (!options[index]?.disabled) {
        setActive(index);
        break;
      }
    }
  }

  return (
    <div ref={root} className={cn('relative min-w-0', className)}>
      {/* Native form participation only: it never displays a browser select popup. */}
      <select
        aria-hidden="true"
        tabIndex={-1}
        name={name}
        value={value}
        required={required}
        disabled={disabled}
        className="pointer-events-none absolute h-px w-px overflow-hidden opacity-0"
        onChange={(event) => {
          onValueChange(event.target.value);
          setInvalid(false);
        }}
        onFocus={() => trigger.current?.focus()}
        onInvalid={(event) => {
          event.preventDefault();
          setInvalid(true);
          trigger.current?.focus();
        }}
      >
        {!options.some((option) => option.value === '') && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      <button
        ref={trigger}
        id={id}
        type="button"
        role="combobox"
        aria-label={label}
        aria-expanded={expanded}
        aria-haspopup="listbox"
        aria-controls={expanded ? `${id}-options` : undefined}
        aria-activedescendant={expanded && active >= 0 ? `${id}-option-${active}` : undefined}
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? `${id}-error` : undefined}
        disabled={disabled}
        onClick={() => (expanded ? setOpen(false) : show())}
        onKeyDown={(event) => {
          if (event.nativeEvent.isComposing) return;
          if (event.key === 'Tab') {
            setOpen(false);
            return;
          }
          if (event.key === 'Escape') {
            if (expanded) {
              event.preventDefault();
              event.stopPropagation();
              setOpen(false);
            }
            return;
          }
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (expanded) select(active);
            else show();
            return;
          }
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (!expanded) show();
            else move(event.key === 'ArrowDown' ? 1 : -1);
            return;
          }
          if (event.key === 'Home' || event.key === 'End') {
            event.preventDefault();
            const indices = options
              .map((option, index) => (option.disabled ? -1 : index))
              .filter((index) => index >= 0);
            const index = (event.key === 'Home' ? indices[0] : indices.at(-1)) ?? -1;
            if (expanded) setActive(index);
            else show(index);
            return;
          }
          if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            const now = Date.now();
            const text =
              now - search.current.time < 700 ? search.current.text + event.key : event.key;
            search.current = { text, time: now };
            const query = text.toLocaleLowerCase();
            const index = options.findIndex(
              (option) => !option.disabled && option.label.toLocaleLowerCase().startsWith(query),
            );
            if (index >= 0) {
              event.preventDefault();
              if (expanded) setActive(index);
              else {
                show(index);
                search.current = { text, time: now };
              }
            }
          }
        }}
        className={cn(
          'flex min-h-[38px] w-full items-center justify-between gap-2 px-3 text-left text-sm transition focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-40',
          appearance === 'filled'
            ? 'rounded-md bg-muted hover:bg-foreground/[0.06]'
            : 'rounded-md border border-border bg-background hover:border-foreground/30 focus-visible:border-foreground/60',
          triggerClassName,
          expanded && (appearance === 'filled' ? 'bg-foreground/[0.08]' : 'border-foreground/60'),
          invalid && (appearance === 'filled' ? 'ring-2 ring-red-400' : 'border-red-400'),
        )}
      >
        <span className={cn('min-w-0 truncate', !selected && 'text-muted-foreground')}>
          {selected?.label ?? placeholder}
        </span>
        <svg
          aria-hidden="true"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={cn(
            'shrink-0 text-muted-foreground transition-transform',
            expanded && 'rotate-180',
          )}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {expanded && (
        <ul
          ref={menu}
          id={`${id}-options`}
          role="listbox"
          aria-label={label}
          popover="manual"
          style={{ ...position, margin: 0, bottom: 'auto', right: 'auto' }}
          className="fixed z-[100] space-y-0.5 overflow-y-auto overscroll-contain rounded-md border border-border bg-background p-1.5 text-foreground shadow-card"
        >
          {options.map((option, index) => (
            <li
              key={option.value}
              id={`${id}-option-${index}`}
              data-index={index}
              role="option"
              aria-selected={option.value === value}
              aria-disabled={option.disabled || undefined}
              onMouseDown={(event) => event.preventDefault()}
              onPointerMove={(event) => {
                if (event.pointerType !== 'touch' && !option.disabled) setActive(index);
              }}
              onClick={() => select(index)}
              className={cn(
                'flex min-h-9 items-center justify-between gap-3 rounded-sm px-3 py-1.5 text-sm',
                option.disabled
                  ? 'cursor-not-allowed text-muted-foreground opacity-50'
                  : 'cursor-pointer',
                // 선택은 배경 없이 굵은 글씨 + 체크 (가리킴의 회색 배경과 겹치지 않게), 나머지는 회색 글씨
                option.value === value
                  ? 'font-bold text-foreground'
                  : 'font-medium text-muted-foreground',
                active === index && 'bg-muted',
              )}
            >
              <span>{option.label}</span>
              {option.value === value && (
                <svg
                  aria-hidden="true"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="shrink-0"
                >
                  <path d="m5 12 4 4 10-10" />
                </svg>
              )}
            </li>
          ))}
        </ul>
      )}
      {invalid && (
        <p id={`${id}-error`} role="alert" className="mt-1 text-xs text-red-700">
          {label}을 선택해 주세요.
        </p>
      )}
    </div>
  );
}
